'use client';

import { useEffect, useState } from 'react';

const GEO_COOKIE = 'nf_geo_country';

const readCountryFromCookie = (): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${GEO_COOKIE}=([^;]+)`),
  );
  return match ? decodeURIComponent(match[1]) : null;
};

export function useGeoCountry() {
  const [country, setCountry] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const cookieValue = readCountryFromCookie();
    if (cookieValue) {
      setCountry(cookieValue);
      try {
        window.localStorage.setItem(GEO_COOKIE, cookieValue);
      } catch {
        // Ignore storage quota failures.
      }
      return;
    }

    try {
      const stored = window.localStorage.getItem(GEO_COOKIE);
      if (stored) {
        setCountry(stored);
      }
    } catch {
      // Ignore storage access failures.
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncFromStorage = (event: StorageEvent) => {
      if (event.key === GEO_COOKIE && typeof event.newValue === 'string') {
        setCountry(event.newValue);
      }
    };

    const syncFromCookie = () => {
      const next = readCountryFromCookie();
      if (next && next !== country) {
        setCountry(next);
        try {
          window.localStorage.setItem(GEO_COOKIE, next);
        } catch {
          // ignore
        }
      }
    };

    window.addEventListener('storage', syncFromStorage);
    document.addEventListener('visibilitychange', syncFromCookie);

    return () => {
      window.removeEventListener('storage', syncFromStorage);
      document.removeEventListener('visibilitychange', syncFromCookie);
    };
  }, [country]);

  return country;
}
